'use client';

import { Button } from '~/components/ui/button';
import { LogoMark } from '~/components/logo-mark';
import { logoutUser } from '~/services/userSessionService';
import { logger } from '~/modules/utilities/cross_env_utils';
import { userSessionStore, useUser } from '~/stores/userSessionStore';
import { Menu, ShieldCheck, X } from 'lucide-react';
import Link from 'next/link';
import { PortalLink } from '~/components/PortalLink';
import { accessibleClients } from '~/modules/client_access';
import { IS_PRODUCTION } from '~/modules/config';
import { portalUrls } from '~/modules/portal_urls';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

const navItems = [
  { href: '/#services', label: 'Services' },
  { href: '/#process', label: 'Process' },
  { href: '/#work', label: 'Work' },
  ...(IS_PRODUCTION ? [] : [{ href: '/devbox', label: 'DevBoxPro' }]),
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const user = useUser();
  const router = useRouter();

  /**
   * `isSigningOut` used to be left `true` forever. The header lives in the root layout and never
   * unmounts, so after one sign-out the flag survived the next sign-in and the button read
   * "Logging out…", disabled, for a freshly signed-in user until a hard reload.
   *
   * A failed request is logged and then treated as a sign-out anyway: the local session is being
   * discarded either way, and leaving the user staring at a dead button is worse than a server
   * session that expires on its own. It used to reject unhandled.
   */
  const signOut = async () => {
    setIsSigningOut(true);
    try {
      await logoutUser();
    } catch (error) {
      logger.error('Sign out request failed; discarding the local session anyway', error);
    } finally {
      userSessionStore.reset();
      setIsSigningOut(false);
      router.replace('/auth/signin');
      router.refresh();
    }
  };

  // The portal is where a client reads their incident reports, setup instructions and sync status.
  // It was reachable only by typing the URL. Secondary to the quote CTA: a visitor is here to hire,
  // a client is here to read.
  // Hovering warms the portal's slowest read, the live status feed, for whichever clients this
  // account may see. A signed-out visitor has nothing to warm and gets a plain link.
  const portalPrefetchUrls = accessibleClients(user?.body?.role, user?.body?.clients).map((client) =>
    portalUrls.syncStatus(client),
  );

  const portalLink = (
    <Button
      className="border border-[#fffdf7]/25 bg-transparent text-[var(--color-paper)] hover:border-[#fffdf7]/45 hover:bg-[#fffdf7]/10 hover:text-[var(--color-paper)]"
      variant="ghost"
      asChild
    >
      <PortalLink href="/client-portal" prefetchUrls={portalPrefetchUrls}>
        Client portal
      </PortalLink>
    </Button>
  );

  // The owner reaches their own portal from every page of the site, beside the client portal. On a narrow desktop
  // the bar has room for its icon only.
  const ownerPortalLink =
    user?.body?.role === 'owner' ? (
      <Button
        className="border border-[#d99a7d]/45 bg-transparent text-[#f0c3ad] hover:border-[#d99a7d]/70 hover:bg-[#fffdf7]/10 hover:text-[var(--color-paper)] md:max-lg:px-3"
        variant="ghost"
        asChild
      >
        <Link href="/owner-portal" aria-label="Owner portal" title="Owner portal">
          <ShieldCheck className="h-4 w-4" aria-hidden />
          <span className="md:max-lg:sr-only">Owner portal</span>
        </Link>
      </Button>
    ) : null;

  const callToAction = user ? (
    <Button
      type="button"
      className="bg-[var(--color-copper)] text-white hover:bg-[var(--color-copper-dark)]"
      disabled={isSigningOut}
      onClick={signOut}
    >
      {isSigningOut ? 'Logging out…' : 'Log out'}
    </Button>
  ) : (
    <Button className="bg-[var(--color-copper)] text-white hover:bg-[var(--color-copper-dark)]" asChild>
      <Link href="/#get-started">Request a quote</Link>
    </Button>
  );

  return (
    <header className="fixed top-0 z-50 flex h-16 w-full items-center border-b border-[#d8cdbb]/25 bg-[#171411]/95 px-4 text-[var(--color-paper)] backdrop-blur lg:px-6">
      <Link className="flex items-center justify-center" href="/">
        <LogoMark />
        <span className="ml-2 text-lg font-semibold tracking-normal">DataConnector-Pro</span>
      </Link>
      <nav className="mx-6 hidden items-center justify-center space-x-5 md:flex">
        {navItems.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className="text-sm font-medium text-[#fffdf7]/78 transition-colors hover:text-[var(--color-paper)]"
          >
            {item.label}
          </Link>
        ))}
      </nav>
      <div className="ml-auto flex items-center space-x-4">
        <div className="hidden md:flex md:items-center md:gap-3">
          {ownerPortalLink}
          {portalLink}
          {callToAction}
        </div>
        <Button
          className="border border-[#fffdf7]/10 bg-transparent text-[var(--color-paper)] hover:bg-[#fffdf7]/10 hover:text-[var(--color-paper)] md:hidden"
          variant="ghost"
          size="icon"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          <span className="sr-only">Toggle menu</span>
        </Button>
      </div>
      {isMenuOpen && (
        <div className="absolute left-0 right-0 top-16 border-b border-[#d8cdbb]/20 bg-[#171411] px-4 py-4 shadow-xl shadow-black/30 md:hidden">
          <nav className="grid gap-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="rounded-md border border-[#d8cdbb]/18 bg-[#211c18] px-4 py-3 text-base font-semibold text-[var(--color-paper)] transition-colors hover:border-[#b76e3c]/55 hover:text-[var(--color-copper)]"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-2 grid gap-2 [&>button]:h-11 [&>button]:w-full [&>a]:h-11 [&>a]:w-full">
              {ownerPortalLink}
              {portalLink}
              {callToAction}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
